import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PRODUCTS, productByKey } from "@/lib/products";
import SanaaMark from "@/components/SanaaMark";
import LangSwitcher from "@/components/LangSwitcher";
import ThemeToggle from "@/components/ThemeToggle";
import ProductMark from "@/components/ProductMark";
import { Check, Loader2, ArrowRight, X, Receipt, CreditCard, LogOut, LayoutDashboard, Sparkles, ExternalLink, Tag, Download, TrendingDown, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import ResetBadge from "@/components/ResetBadge";

export default function Billing() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [plans, setPlans] = useState(null);
  const [subs, setSubs] = useState([]);
  const [txs, setTxs] = useState([]);
  const [busy, setBusy] = useState(null);
  const [activeProduct, setActiveProduct] = useState("creative");
  const [mode, setMode] = useState(null);
  const [interval, setInterval] = useState("month");
  const [portalLoading, setPortalLoading] = useState(false);
  const [annualOffers, setAnnualOffers] = useState([]);

  useEffect(() => {
    refresh();
    api.get("/billing/mode").then((r) => setMode(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (params.get("canceled")) toast.info("Checkout canceled — you can subscribe any time.");
  }, [params]);

  const refresh = async () => {
    try {
      const [p, s, t, a] = await Promise.all([
        api.get("/billing/plans"),
        api.get("/billing/subscriptions"),
        api.get("/billing/transactions"),
        api.get("/billing/recommend-annual").catch(() => ({ data: [] })),
      ]);
      setPlans(p.data);
      setSubs(s.data);
      setTxs(t.data);
      setAnnualOffers(a.data || []);
    } catch {
      toast.error("Failed to load billing data");
    }
  };

  const upgradeToAnnual = async (offer) => {
    setBusy(`${offer.product}/${offer.tier}/year`);
    try {
      const r = await api.post("/billing/checkout", {
        product: offer.product, tier: offer.tier, interval: "year",
        origin_url: window.location.origin,
      });
      window.location.href = r.data.url;
    } catch (e) {
      setBusy(null);
      toast.error(e?.response?.data?.detail || "Could not start upgrade");
    }
  };

  const subscribe = async (product, tier) => {
    setBusy(`${product}/${tier}`);
    try {
      const r = await api.post("/billing/checkout", {
        product, tier, interval,
        origin_url: window.location.origin,
      });
      window.location.href = r.data.url;
    } catch (e) {
      setBusy(null);
      toast.error(e?.response?.data?.detail || "Could not start checkout");
    }
  };

  const cancel = async (sub) => {
    if (!confirm(`Cancel ${sub.plan_name}? Access remains until ${new Date(sub.current_period_end).toLocaleDateString()}.`)) return;
    try {
      await api.post(`/billing/cancel/${sub.id}`);
      toast.success("Subscription will end at period close");
      refresh();
    } catch {
      toast.error("Cancel failed");
    }
  };

  const resume = async (sub) => {
    try {
      await api.post(`/billing/resume/${sub.id}`);
      toast.success("Subscription resumed");
      refresh();
    } catch {
      toast.error("Resume failed");
    }
  };

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const r = await api.post("/billing/portal");
      window.location.href = r.data.url;
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "object" ? detail.message : (detail || "Portal unavailable");
      toast.info(msg);
    } finally {
      setPortalLoading(false);
    }
  };

  const activeSubFor = (product) => subs.find((s) => s.product === product && s.status === "active");
  const productMeta = productByKey(activeProduct);
  const productPlans = plans?.[activeProduct] || [];
  const isRealStripe = mode?.mode === "subscription";

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-30 border-b border-border/60 bg-background/70 backdrop-blur-xl">
        <div className="mx-auto max-w-7xl px-5 py-4 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2">
            <SanaaMark size={26} />
            <span className="font-display tracking-tight">Sanaa AI</span>
          </Link>
          <div className="flex items-center gap-2">
            <button
              onClick={() => nav("/app")}
              data-testid="billing-back-suite"
              className="inline-flex h-9 items-center gap-2 rounded-sm border border-border/60 bg-card/40 hover:bg-card px-3 text-sm"
            >
              <LayoutDashboard className="h-4 w-4" /> <span className="hidden sm:inline">Suite</span>
            </button>
            <LangSwitcher />
            <ThemeToggle />
            <button
              data-testid="billing-logout"
              onClick={() => { logout(); nav("/"); }}
              aria-label="Logout"
              className="h-9 w-9 inline-flex items-center justify-center rounded-sm border border-border/60 bg-card/40 hover:bg-card"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-5 pt-12 pb-24">
        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">Billing</div>
        <h1 className="font-serif text-5xl sm:text-6xl tracking-tight leading-[1]">Manage your suite.</h1>
        <p className="mt-4 text-muted-foreground text-lg max-w-2xl">
          Subscribe to each Sanaa AI product independently. All payments processed by Stripe — cancel any time.
        </p>

        {/* Customer Portal CTA — only useful when real Stripe key + an active sub */}
        {(subs.length > 0 || user?.is_admin) && (
          <div className="mt-8 flex flex-wrap items-center gap-3">
            {subs.length > 0 && (
              <button
                data-testid="billing-portal"
                onClick={openPortal}
                disabled={portalLoading}
                className="inline-flex h-10 items-center gap-2 rounded-sm border border-border bg-card hover:bg-secondary px-4 text-sm font-medium disabled:opacity-60"
              >
                {portalLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ExternalLink className="h-4 w-4" />}
                Manage in Stripe Customer Portal
              </button>
            )}
            {user?.is_admin && (
              <button
                data-testid="billing-admin"
                onClick={() => nav("/app/admin")}
                className="inline-flex h-10 items-center gap-2 rounded-sm border border-border bg-card hover:bg-secondary px-4 text-sm font-medium"
              >
                <ShieldCheck className="h-4 w-4" /> Admin · coupons & emails
              </button>
            )}
            {!isRealStripe && subs.length > 0 && (
              <span className="inline-flex items-center text-xs text-muted-foreground">
                (Portal activates once a real Stripe key is configured)
              </span>
            )}
          </div>
        )}

        {/* Annual upgrade banner */}
        {annualOffers.length > 0 && (
          <section data-testid="annual-upgrade-banner" className="mt-10 rounded-sm border border-border bg-card p-5 sm:p-6 relative overflow-hidden">
            <div
              className="absolute -end-32 -top-20 h-64 w-64 rounded-full blur-3xl opacity-30"
              style={{ background: "linear-gradient(135deg, #16a34a, #22c55e)" }}
            />
            <div className="relative flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em]" style={{ color: "#16a34a" }}>
                  <TrendingDown className="h-3 w-3" />
                  Save by switching to annual
                </div>
                <h3 className="mt-2 font-serif text-2xl sm:text-3xl tracking-tight">
                  Save{" "}
                  <span style={{ color: "#16a34a" }}>
                    ${annualOffers.reduce((s, o) => s + o.savings, 0).toLocaleString()}
                  </span>{" "}
                  by switching {annualOffers.length === 1 ? annualOffers[0].plan_name : `${annualOffers.length} subscriptions`} to annual.
                </h3>
                <p className="mt-2 text-sm text-muted-foreground max-w-2xl">
                  Pay once a year instead of monthly — you skip 2 months of billing on every plan. Cancel any time.
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                {annualOffers.map((o) => (
                  <button
                    key={o.subscription_id}
                    data-testid={`annual-upgrade-${o.product}`}
                    onClick={() => upgradeToAnnual(o)}
                    disabled={busy === `${o.product}/${o.tier}/year`}
                    className="inline-flex h-10 items-center gap-2 rounded-sm px-4 text-sm font-medium text-white disabled:opacity-60"
                    style={{ background: "linear-gradient(135deg, #16a34a, #22c55e)" }}
                  >
                    {busy === `${o.product}/${o.tier}/year` ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        Upgrade {o.plan_name} · save ${o.savings.toLocaleString()}
                        <ArrowRight className="h-3.5 w-3.5 rtl:rotate-180" />
                      </>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Active subscriptions */}
        <section className="mt-12">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-xl">Your subscriptions</h2>
            <span className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
              {subs.filter((s) => s.status === "active").length} active
            </span>
          </div>
          {subs.length === 0 ? (
            <div className="rounded-sm border border-dashed border-border/60 bg-card/40 p-10 text-center">
              <Sparkles className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
              <div className="font-display text-lg">No subscriptions yet</div>
              <p className="text-sm text-muted-foreground mt-1">Pick a product below to get started — the Free tier is one click.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {subs.map((s) => {
                const p = productByKey(s.product);
                const cancelPending = s.cancel_at_period_end;
                return (
                  <div key={s.id} data-testid={`sub-card-${s.product}`} className="rounded-sm border border-border/60 bg-card p-5">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        {p && <ProductMark product={p} size={36} />}
                        <div>
                          <div className="font-display text-base">{p?.name || s.product}</div>
                          <div className="text-xs uppercase tracking-[0.16em] text-muted-foreground">
                            {s.plan_name} · {s.interval === "year" ? "Annual" : "Monthly"}
                          </div>
                        </div>
                      </div>
                      <span
                        className="text-[10px] uppercase tracking-[0.16em] px-2 py-0.5 rounded-sm"
                        style={
                          cancelPending
                            ? { background: "#f9731622", color: "#f97316" }
                            : s.status === "active"
                            ? { background: "#16a34a22", color: "#16a34a" }
                            : { background: "hsl(var(--muted))", color: "hsl(var(--muted-foreground))" }
                        }
                      >
                        {cancelPending ? "Ending" : s.status}
                      </span>
                    </div>
                    <div className="mt-5 flex items-center justify-between gap-3 text-xs text-muted-foreground">
                      <span>
                        {cancelPending ? "Ends" : "Renews"}{" "}
                        <span className="text-foreground font-medium">
                          {new Date(s.current_period_end).toLocaleDateString()}
                        </span>
                      </span>
                      <ResetBadge periodEnd={s.current_period_end} label="Quota resets" />
                    </div>
                    {s.status === "active" && (
                      cancelPending ? (
                        <button
                          data-testid={`sub-resume-${s.product}`}
                          onClick={() => resume(s)}
                          className="mt-4 inline-flex h-8 items-center gap-1.5 rounded-sm border border-border px-2.5 text-xs hover:bg-secondary"
                        >
                          Resume subscription
                        </button>
                      ) : (
                        <button
                          data-testid={`sub-cancel-${s.product}`}
                          onClick={() => cancel(s)}
                          className="mt-4 inline-flex h-8 items-center gap-1.5 rounded-sm border border-border px-2.5 text-xs hover:bg-secondary"
                        >
                          <X className="h-3 w-3" /> Cancel at period end
                        </button>
                      )
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Plan selector */}
        <section className="mt-16">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
            <h2 className="font-display text-xl">Browse plans</h2>
            {/* Monthly/Annual toggle */}
            <div data-testid="interval-toggle" className="inline-flex items-center rounded-sm border border-border bg-card p-0.5 text-sm">
              <button
                data-testid="interval-month"
                onClick={() => setInterval("month")}
                className={`h-8 px-3.5 rounded-sm font-medium transition-colors ${
                  interval === "month" ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                Monthly
              </button>
              <button
                data-testid="interval-year"
                onClick={() => setInterval("year")}
                className={`h-8 px-3.5 rounded-sm font-medium transition-colors flex items-center gap-2 ${
                  interval === "year" ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                Annual
                <span
                  className="text-[10px] uppercase tracking-[0.16em] px-1.5 py-0.5 rounded-sm"
                  style={{
                    background: interval === "year" ? "rgba(255,255,255,0.18)" : "#16a34a22",
                    color: interval === "year" ? "currentColor" : "#16a34a",
                  }}
                >
                  Save 2 mo
                </span>
              </button>
            </div>
          </div>

          {/* Product tabs */}
          <div className="flex gap-2 mb-6 overflow-x-auto pb-1">
            {PRODUCTS.map((p) => {
              const active = activeProduct === p.key;
              return (
                <button
                  key={p.key}
                  data-testid={`billing-tab-${p.key}`}
                  onClick={() => setActiveProduct(p.key)}
                  className={`shrink-0 inline-flex items-center gap-2 rounded-sm border px-3.5 h-10 text-sm font-medium transition-colors ${
                    active ? "border-foreground bg-card" : "border-border/60 bg-card/40 text-muted-foreground hover:text-foreground"
                  }`}
                  style={active ? { borderColor: p.accent } : undefined}
                >
                  <ProductMark product={p} size={22} />
                  {p.name}
                </button>
              );
            })}
          </div>

          {/* Tiers grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {productPlans.map((t) => {
              const myActive = activeSubFor(activeProduct);
              const isCurrent = myActive?.tier === t.tier && myActive?.status === "active" && (myActive?.interval || "month") === interval;
              const key = `${activeProduct}/${t.tier}`;
              const isFree = t.price_monthly === 0;
              const displayPrice = interval === "year" ? t.price_annual : t.price_monthly;
              const perMonth = interval === "year" && !isFree ? (t.price_annual / 12) : null;
              return (
                <div
                  key={t.tier}
                  data-testid={`plan-card-${activeProduct}-${t.tier}`}
                  className={`relative rounded-sm border p-6 transition-colors ${
                    t.highlight ? "border-foreground bg-card" : "border-border/60 bg-card/50 hover:border-border"
                  }`}
                >
                  {t.highlight && (
                    <span
                      className="absolute -top-2.5 left-5 px-2 py-0.5 text-[10px] uppercase tracking-[0.16em] font-medium text-white rounded-sm"
                      style={{ background: productMeta.gradient }}
                    >
                      Most popular
                    </span>
                  )}
                  <div className="text-sm uppercase tracking-[0.18em] text-muted-foreground">{t.tier_label}</div>
                  <div className="mt-2 flex items-baseline gap-1">
                    <span className="font-serif text-4xl tracking-tight">
                      {isFree ? "Free" : `$${displayPrice.toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
                    </span>
                    {!isFree && <span className="text-sm text-muted-foreground">/{interval === "year" ? "yr" : "mo"}</span>}
                  </div>
                  {perMonth && (
                    <div className="text-[11px] text-muted-foreground mt-0.5">
                      ${perMonth.toFixed(0)}/mo billed annually
                    </div>
                  )}
                  <div className="mt-1 text-xs text-muted-foreground">{t.name}</div>
                  <ul className="mt-5 space-y-1.5 text-sm">
                    {Object.entries(t.features).map(([k, v]) => (
                      <li key={k} className="flex items-start gap-2">
                        <Check className="h-3.5 w-3.5 mt-0.5 shrink-0" style={{ color: productMeta.accent }} />
                        <span>
                          <span className="font-medium">{v === -1 ? "Unlimited" : v.toLocaleString()}</span>{" "}
                          <span className="text-muted-foreground">{k.replace(/_/g, " ")}</span>
                        </span>
                      </li>
                    ))}
                  </ul>
                  <button
                    data-testid={`plan-subscribe-${activeProduct}-${t.tier}`}
                    onClick={() => subscribe(activeProduct, t.tier)}
                    disabled={busy === key || isCurrent}
                    className={`mt-6 inline-flex w-full h-10 items-center justify-center gap-2 rounded-sm text-sm font-medium transition-all disabled:opacity-60 ${
                      isCurrent
                        ? "bg-secondary text-foreground"
                        : t.highlight
                        ? "text-white"
                        : "bg-foreground text-background hover:opacity-90"
                    }`}
                    style={t.highlight && !isCurrent ? { background: productMeta.gradient } : undefined}
                  >
                    {busy === key ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : isCurrent ? (
                      <>Current plan</>
                    ) : isFree ? (
                      <>Activate free <ArrowRight className="h-4 w-4 rtl:rotate-180" /></>
                    ) : (
                      <>Subscribe <ArrowRight className="h-4 w-4 rtl:rotate-180" /></>
                    )}
                  </button>
                </div>
              );
            })}
          </div>

          {/* Promo code hint */}
          {isRealStripe && (
            <div className="mt-4 inline-flex items-center gap-2 text-xs text-muted-foreground">
              <Tag className="h-3 w-3" />
              Have a promo code? Enter it at checkout — promotion codes are enabled on every paid plan.
            </div>
          )}
        </section>

        {/* Invoices */}
        <section className="mt-16">
          <h2 className="font-display text-xl mb-4 flex items-center gap-2">
            <Receipt className="h-4 w-4" /> Payment history
          </h2>
          {txs.length === 0 ? (
            <div className="rounded-sm border border-dashed border-border/60 bg-card/40 p-8 text-center text-sm text-muted-foreground">
              No payments yet.
            </div>
          ) : (
            <div className="rounded-sm border border-border/60 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-secondary/60">
                  <tr className="text-start">
                    <th className="text-start font-medium px-4 py-2.5 text-xs uppercase tracking-[0.16em] text-muted-foreground">Date</th>
                    <th className="text-start font-medium px-4 py-2.5 text-xs uppercase tracking-[0.16em] text-muted-foreground">Plan</th>
                    <th className="text-start font-medium px-4 py-2.5 text-xs uppercase tracking-[0.16em] text-muted-foreground">Amount</th>
                    <th className="text-start font-medium px-4 py-2.5 text-xs uppercase tracking-[0.16em] text-muted-foreground">Status</th>
                    <th className="text-start font-medium px-4 py-2.5 text-xs uppercase tracking-[0.16em] text-muted-foreground">Invoice</th>
                  </tr>
                </thead>
                <tbody>
                  {txs.map((t) => (
                    <tr key={t.id} className="border-t border-border/60" data-testid={`tx-${t.session_id}`}>
                      <td className="px-4 py-2.5 text-muted-foreground">{new Date(t.created_at).toLocaleString()}</td>
                      <td className="px-4 py-2.5">{t.plan_name}</td>
                      <td className="px-4 py-2.5 font-mono">${t.amount.toFixed(2)}</td>
                      <td className="px-4 py-2.5">
                        <span
                          className="inline-block px-2 py-0.5 rounded-sm text-[11px] uppercase tracking-wider"
                          style={
                            t.payment_status === "paid"
                              ? { background: "#16a34a22", color: "#16a34a" }
                              : t.payment_status === "unpaid"
                              ? { background: "hsl(var(--muted))", color: "hsl(var(--muted-foreground))" }
                              : { background: "#ef444422", color: "#ef4444" }
                          }
                        >
                          {t.payment_status}
                        </span>
                      </td>
                      <td className="px-4 py-2.5">
                        {t.invoice_pdf ? (
                          <a
                            href={t.invoice_pdf}
                            target="_blank"
                            rel="noreferrer"
                            data-testid={`tx-invoice-${t.session_id}`}
                            className="inline-flex items-center gap-1 text-xs text-foreground hover:opacity-70"
                            title="Download PDF"
                          >
                            <Download className="h-3.5 w-3.5" /> PDF
                          </a>
                        ) : t.hosted_invoice_url ? (
                          <a
                            href={t.hosted_invoice_url}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 text-xs text-foreground hover:opacity-70"
                          >
                            <ExternalLink className="h-3.5 w-3.5" /> View
                          </a>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <div className="mt-16 rounded-sm border border-border/60 bg-card/40 p-5 flex items-start gap-3">
          <CreditCard className="h-4 w-4 mt-0.5 text-muted-foreground" />
          <div className="text-sm text-muted-foreground">
            {isRealStripe ? (
              <>
                <span className="text-foreground font-medium">Live Stripe Subscriptions.</span> Real recurring billing
                via Stripe Subscriptions API
                {mode?.webhook_secret_set ? (
                  <> · webhook signature verification <span style={{ color: "#16a34a" }}>enabled</span>.</>
                ) : (
                  <> · <span style={{ color: "#f97316" }}>STRIPE_WEBHOOK_SECRET not set</span> — add it before launch.</>
                )}
              </>
            ) : (
              <>
                <span className="text-foreground font-medium">Stripe Test Mode (simulation).</span> Payments are processed
                via Emergent's Stripe test routing — use card{" "}
                <code className="font-mono text-foreground bg-secondary/60 px-1.5 py-0.5 rounded-sm text-xs">4242 4242 4242 4242</code>{" "}
                with any future expiry and any CVC. Swap{" "}
                <code className="font-mono text-foreground bg-secondary/60 px-1.5 py-0.5 rounded-sm text-xs">STRIPE_API_KEY</code>{" "}
                for a real Stripe key to activate true recurring auto-renewal, the Customer Portal, and promo codes — code path is already wired.
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
